import { useMemo, useState } from "react";
import { koshaInfo, getInsight } from "@/lib/quizData";
import { Download, Calendar, TrendingUp, TrendingDown, Sparkles, Loader2, FileDown } from "lucide-react";
import siwonLogo from "@/assets/siwon-logo.png";
import { generateResultsPDF } from "@/services/pdfService";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";

export const ResultsSection = ({ results, userName }) => {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const handleDownloadPDF = async () => {
    setIsGeneratingPdf(true);
    try {
      await generateResultsPDF(results, userName, "Relationship");
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const chartData = useMemo(() => [
    { kosha: 'Physical', value: results.annamaya, fullMark: 100 },
    { kosha: 'Energy', value: results.pranamaya, fullMark: 100 },
    { kosha: 'Mind', value: results.manomaya, fullMark: 100 },
    { kosha: 'Wisdom', value: results.vijnanamaya, fullMark: 100 },
    { kosha: 'Bliss', value: results.anandamaya, fullMark: 100 },
  ], [results]);

  const koshaResults = [
    { key: 'annamaya', score: results.annamaya },
    { key: 'pranamaya', score: results.pranamaya },
    { key: 'manomaya', score: results.manomaya },
    { key: 'vijnanamaya', score: results.vijnanamaya },
    { key: 'anandamaya', score: results.anandamaya },
  ];

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-accent';
    if (score >= 60) return 'text-secondary';
    if (score >= 40) return 'text-orange-500';
    return 'text-destructive';
  };

  const getScoreBarColor = (score) => {
    if (score >= 80) return 'bg-accent';
    if (score >= 60) return 'bg-secondary';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-destructive';
  };

  return (
    <section id="results" className="py-20 md:py-28 bg-gradient-to-b from-sage-dark to-background mandala-bg" data-testid="results-section">
      <div className="container">
        <div id="results-pdf-content" className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 animate-fade-up">
            <img src={siwonLogo} alt="Siwon Galaxy" className="h-16 mx-auto mb-6" />
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
              Your Relationship Diagnostic Report
            </h2>
            <p className="text-lg text-muted-foreground mb-4">
              Welcome, <span className="font-semibold text-accent">{userName}</span>. Here's your personalized Five Koshas analysis.
            </p>
            
            {/* Download PDF Button */}
            <button
              onClick={handleDownloadPDF}
              disabled={isGeneratingPdf}
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-full font-medium transition-all disabled:opacity-50"
              data-testid="download-pdf-btn"
            >
              {isGeneratingPdf ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating PDF...
                </>
              ) : (
                <>
                  <FileDown className="w-5 h-5" />
                  Download PDF Report
                </>
              )}
            </button>
          </div>

          {/* Overall Score Card */}
          <div className="card-spiritual text-center mb-12 animate-scale-in">
            <div className="inline-flex items-center gap-2 bg-accent/20 rounded-full px-4 py-2 mb-4">
              <Sparkles className="w-4 h-4 text-accent" />
              <span className="text-sm font-medium text-primary">Overall Harmony Score</span>
            </div>
            <div className={`font-serif text-6xl md:text-7xl font-bold mb-6 ${getScoreColor(results.overall)}`} data-testid="overall-score">
              {results.overall}%
            </div>
            <p className="text-muted-foreground max-w-md mx-auto">
              {results.overall >= 70
                ? "Your relationship shows beautiful harmony across the Five Koshas. Continue nurturing this sacred bond."
                : results.overall >= 50
                ? "Your relationship has a solid foundation with room for deeper connection. Small, consistent efforts will yield great rewards."
                : "Your relationship is calling for attention and care. With guidance, you can transform these challenges into growth."}
            </p>
          </div>

          {/* Radar Chart */}
          <div className="card-spiritual mb-12">
            <h3 className="font-serif text-2xl font-semibold text-primary text-center mb-6">
              Five Koshas Balance
            </h3>
            <div className="h-80 md:h-96 w-full" style={{ minHeight: '320px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={chartData}>
                  <PolarGrid stroke="hsl(var(--border))" />
                  <PolarAngleAxis 
                    dataKey="kosha" 
                    tick={{ fill: 'hsl(var(--foreground))', fontSize: 12 }}
                  />
                  <PolarRadiusAxis 
                    angle={90} 
                    domain={[0, 100]} 
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                  />
                  <Radar
                    name="Your Score"
                    dataKey="value"
                    stroke="hsl(var(--accent))"
                    fill="hsl(var(--accent))"
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Individual Kosha Results */}
          <div className="space-y-6 mb-12">
            {koshaResults.map(({ key, score }) => {
              const info = koshaInfo[key];
              const insight = getInsight(key, score);
              
              return (
                <div key={key} className="card-spiritual animate-fade-up">
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    {/* Kosha Info */}
                    <div className="md:w-1/3">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-3xl">{info.icon}</span>
                        <div>
                          <h4 className="font-serif text-xl font-semibold text-primary">
                            {info.name}
                          </h4>
                          <p className="text-sm text-muted-foreground">{info.subtitle}</p>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {info.description}
                      </p>
                    </div>

                    {/* Score & Insight */}
                    <div className="md:w-2/3">
                      {/* Progress Bar */}
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className={`font-bold text-2xl ${getScoreColor(score)}`}>
                            {score}%
                          </span>
                          <span className={`flex items-center gap-1 text-sm font-medium ${
                            insight.type === 'strength' ? 'text-accent' : 'text-orange-500'
                          }`}>
                            {insight.type === 'strength' ? (
                              <><TrendingUp className="w-4 h-4" /> Strength</>
                            ) : (
                              <><TrendingDown className="w-4 h-4" /> Opportunity</>
                            )}
                          </span>
                        </div>
                        <div className="h-3 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full ${getScoreBarColor(score)} transition-all duration-700 rounded-full`}
                            style={{ width: `${score}%` }}
                          />
                        </div>
                      </div>

                      {/* Insight */}
                      <p className="text-foreground mb-3">{insight.message}</p>
                      <blockquote className="border-l-4 border-accent pl-4 italic text-muted-foreground text-sm">
                        "{insight.quote}"
                      </blockquote>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA Section */}
          <div className="card-spiritual bg-gradient-to-br from-primary/10 to-accent/10 text-center">
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-primary mb-4">
              Ready for Deeper Transformation?
            </h3>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Your diagnostic reveals unique patterns. Connect with Vinod Kumar for personalized coaching 
              to harmonize your relationship across all Five Koshas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://chat.whatsapp.com/Ie7aaYNHDiOGPyZEPvxhfW"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-lime inline-flex items-center justify-center gap-2"
              >
                <Calendar className="w-5 h-5" />
                Book a Free Consultation
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResultsSection;
